import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCalendars } from '../../contexts/CalendarContext';
import { CALENDAR_COLORS } from '../../services/calendarStorage';
import { getUserProfile } from '../../services/userService';
import CalendarShareModal from './CalendarShareModal';
import Button from '../Common/Button';
import Input from '../Common/Input';
import { logCalendarCreated, logCalendarEdited, logCalendarDeleted, logCalendarShared } from '../../services/analytics';

const CalendarManager = ({ isOpen, onClose }) => {
    const { currentUser } = useAuth();
    const {
        calendars,
        selectedCalendar,
        createCalendar,
        updateCalendar,
        deleteCalendar,
        leaveCalendar,
        isOwner,
        canEdit
    } = useCalendars();

    const [mode, setMode] = useState('list'); // 'list', 'create', 'edit'
    const [editingCalendar, setEditingCalendar] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        color: 'blue'
    });
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [sharingCalendar, setSharingCalendar] = useState(null);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handleClose = () => {
        setMode('list');
        setEditingCalendar(null);
        setFormData({ name: '', description: '', color: 'blue' });
        setError('');
        onClose();
    };

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                handleClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleCreateNew = () => {
        setMode('create');
        setFormData({ name: '', description: '', color: 'blue' });
        setError('');
    };

    const handleEdit = (calendar) => {
        setEditingCalendar(calendar);
        setFormData({
            name: calendar.name,
            description: calendar.description || '',
            color: calendar.color
        });
        setMode('edit');
        setError('');
    };

    const handleShare = (calendar) => {
        setSharingCalendar(calendar);
        setShowShareModal(true);
        logCalendarShared();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            setError('Calendar name is required');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            if (mode === 'create') {
                // Fetch user's actual tier from Firebase
                const profile = await getUserProfile(currentUser.uid);
                const userTier = profile?.userTier || 'free';
                await createCalendar(formData, userTier);
                logCalendarCreated({ color: formData.color });
            } else if (mode === 'edit') {
                await updateCalendar(editingCalendar.id, formData);
                logCalendarEdited();
            }
            setMode('list');
            setEditingCalendar(null);
            setFormData({ name: '', description: '', color: 'blue' });
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (calendarId) => {
        if (!window.confirm('Are you sure you want to delete this calendar? All tasks in this calendar will be deleted.')) {
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            await deleteCalendar(calendarId);
            logCalendarDeleted();
            setMode('list');
        } catch (err) {
            setError(err.message || 'Failed to delete calendar');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLeave = async (calendarId) => {
        if (!window.confirm('Are you sure you want to leave this calendar?')) {
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            await leaveCalendar(calendarId);
            setMode('list');
        } catch (err) {
            setError(err.message || 'Failed to leave calendar');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getCalendarColorClass = (color) => {
        const colors = {
            blue: 'bg-blue-500',
            green: 'bg-green-500',
            purple: 'bg-purple-500',
            red: 'bg-red-500',
            orange: 'bg-orange-500',
            pink: 'bg-pink-500',
            teal: 'bg-teal-500',
            indigo: 'bg-indigo-500'
        };
        return colors[color] || colors.blue;
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 overflow-y-auto animate-fade-in"
            role="dialog"
            aria-modal="true"
            aria-labelledby="calendar-manager-title"
        >
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={handleClose}
                aria-hidden="true"
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div
                    className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 animate-fade-in transition-colors duration-200"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close button */}
                    <button
                        onClick={handleClose}
                        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        aria-label="Close modal"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Content */}
                    {mode === 'list' && (
                        <div>
                            <div className="flex items-center justify-between mb-6">
                                <h2 id="calendar-manager-title" className="text-2xl font-bold text-gray-900 dark:text-white">
                                    Manage Calendars
                                </h2>
                                <Button onClick={handleCreateNew} variant="primary">
                                    <span className="flex items-center space-x-2">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        <span>New Calendar</span>
                                    </span>
                                </Button>
                            </div>

                            {error && (
                                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                                </div>
                            )}

                            <div className="space-y-3">
                                {calendars.map((calendar) => (
                                    <div
                                        key={calendar.id}
                                        className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                    >
                                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                                            <div className={`w-4 h-4 rounded-full flex-shrink-0 ${getCalendarColorClass(calendar.color)}`} />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center space-x-2">
                                                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                                                        {calendar.name}
                                                    </h3>
                                                    {isOwner(calendar) && (
                                                        <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 rounded">
                                                            Owner
                                                        </span>
                                                    )}
                                                    {selectedCalendar?.id === calendar.id && (
                                                        <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded">
                                                            Active
                                                        </span>
                                                    )}
                                                </div>
                                                {calendar.description && (
                                                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                                        {calendar.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-2 ml-4">
                                            {isOwner(calendar) && (
                                                <button
                                                    onClick={() => handleShare(calendar)}
                                                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                                    aria-label="Share calendar"
                                                    title="Share calendar"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                                    </svg>
                                                </button>
                                            )}

                                            {canEdit(calendar) && (
                                                <button
                                                    onClick={() => handleEdit(calendar)}
                                                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                                    aria-label="Edit calendar"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                            )}

                                            {isOwner(calendar) && (
                                                <button
                                                    onClick={() => handleDelete(calendar.id)}
                                                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                                    aria-label="Delete calendar"
                                                    disabled={isSubmitting}
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            )}

                                            {!isOwner(calendar) && (
                                                <button
                                                    onClick={() => handleLeave(calendar.id)}
                                                    className="px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                                    disabled={isSubmitting}
                                                >
                                                    Leave
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                {calendars.length === 0 && (
                                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                        <p>No calendars yet. Create your first calendar!</p>
                                    </div>
                                )}
                            </div>

                            <div className="mt-6 flex justify-end">
                                <Button variant="ghost" onClick={handleClose}>
                                    Close
                                </Button>
                            </div>
                        </div>
                    )}

                    {(mode === 'create' || mode === 'edit') && (
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                                {mode === 'create' ? 'Create Calendar' : 'Edit Calendar'}
                            </h2>

                            {error && (
                                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <Input
                                    label="Calendar Name"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="e.g., Work, Personal, Team"
                                    required
                                />

                                <Input
                                    label="Description"
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Optional description"
                                />

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Color
                                    </label>
                                    <div className="grid grid-cols-4 gap-3">
                                        {CALENDAR_COLORS.map((colorOption) => (
                                            <button
                                                key={colorOption.value}
                                                type="button"
                                                onClick={() => setFormData(prev => ({ ...prev, color: colorOption.value }))}
                                                className={`flex items-center space-x-2 p-3 rounded-lg border-2 transition-all ${formData.color === colorOption.value
                                                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                                    }`}
                                            >
                                                <div className={`w-6 h-6 rounded-full ${getCalendarColorClass(colorOption.value)}`} />
                                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                    {colorOption.name}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex justify-end space-x-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        onClick={() => setMode('list')}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create' : 'Save Changes'}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>

            {/* Calendar Share Modal */}
            <CalendarShareModal
                isOpen={showShareModal}
                onClose={() => {
                    setShowShareModal(false);
                    setSharingCalendar(null);
                }}
                calendar={sharingCalendar}
            />
        </div>
    );
};

export default CalendarManager;
